import { useMotionConfig } from "@nivo/core";
import { DefaultLink, DefaultNode, SankeyCommonProps, SankeyNodeDatum } from "@nivo/sankey";
import { useTheme } from "@nivo/theming";
import { animated, useSprings } from "@react-spring/web";

interface SankeyLayerLabelProps<N extends DefaultNode, L extends DefaultLink> {
  nodes: SankeyNodeDatum<N, L>[];
  layerLabels: string[];
  labelOrientation: SankeyCommonProps<N, L>["labelOrientation"];
  getLabelTextColor: (node: SankeyNodeDatum<N, L>) => string;
}

interface LayerLabel {
  id: string;
  x: number;
  y: number;
  textAnchor: string;
  color: string;
  label: string;
}

interface LabelPosition {
  layer: number;
  pos: number;
  textAnchor: string;
  color: string;
}

const SankeyLayerLabels = <N extends DefaultNode, L extends DefaultLink>(props: SankeyLayerLabelProps<N, L>) => {
  const maxLayer = props.nodes.reduce((acc, node) => {
    return Math.max(node.layer, acc);
  }, 0);
  const { animate, config: springConfig } = useMotionConfig();
  const theme = useTheme();

  const labelRotation = props.labelOrientation === "vertical" ? -90 : 0;
  const positions = props.nodes
    .map((node: any) => {
      if (node.layer === 0) {
        return {
          layer: node.layer,
          pos: node.x,
          textAnchor: "start",
          color: props.getLabelTextColor(node),
        } as LabelPosition;
      } else if (node.layer === maxLayer) {
        return {
          layer: node.layer,
          pos: node.x1,
          textAnchor: "end",
          color: props.getLabelTextColor(node),
        } as LabelPosition;
      } else {
        return {
          layer: node.layer,
          pos: node.x + (node.x1 - node.x0) * 0.5,
          textAnchor: "middle",
          color: props.getLabelTextColor(node),
        } as LabelPosition;
      }
    })
    .filter((x: LabelPosition, i: number, arr: LabelPosition[]) => {
      return arr.findIndex((a) => a.layer === x.layer) === i;
    })
    .sort(function (a: any, b: LabelPosition) {
      return a.pos - b.pos;
    });
  const labels = positions.map((position: LabelPosition, index: number) => {
    return {
      id: `layer-label-${index}`,
      x: position.pos,
      y: -theme.labels.text.fontSize,
      textAnchor: position.textAnchor,
      color: position.color,
      label: props.layerLabels[index],
    } as LayerLabel;
  });

  const springs = useSprings(
    labels.length,
    labels.map((label) => ({
      transform: `translate(${label.x}, ${label.y}) rotate(${labelRotation})`,
      color: label.color,
      config: springConfig,
      immediate: !animate,
    })),
  );

  return (
    <>
      {springs.map((animatedProps: any, index: number) => {
        const label = labels[index];

        return (
          <animated.text
            key={label.id}
            dominantBaseline="central"
            textAnchor={label.textAnchor as "start" | "middle" | "end" | "inherit"}
            transform={animatedProps.transform}
            style={{
              ...theme.labels.text,
              fill: animatedProps.color,
              pointerEvents: "none",
            }}
          >
            {label.label}
          </animated.text>
        );
      })}
    </>
  );
};

export default SankeyLayerLabels;
